import React from "react";
import styles from "./SingleItem.module.css";
import SearchTips from "../components/SearchTips";
import SuggestionInput from "shared/@forms/SuggestionInput";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import StatsTotalCard from "../components/StatsTotalCard";
import CustomerActivityGraph from "../components/CustomerActivityGraph";
import StatsPieChart from "../components/StatsPieChart";
import { stats as statsApi, errorMessage } from "shared/api";

export function SingleItem() {
  const [query, setQuery] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [itemStats, setItemStats] = React.useState(null);
  const [statsLoading, setStatsLoading] = React.useState(false);
  const [statsError, setStatsError] = React.useState(null);
  const searchReqRef = React.useRef(0);
  const statsReqRef = React.useRef(0);

  const fetchSuggestions = React.useCallback((value) => {
    // The search tips offer `Product:<name>` and `SKU:<number>`; the API
    // takes the bare term.
    const normalized = value
      .trim()
      .replace(/^(product|sku):\s*/i, "")
      .toLowerCase();

    if (!normalized) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const reqId = ++searchReqRef.current;
    setIsLoading(true);

    statsApi
      .searchItems(normalized)
      .then((matches) => {
        if (reqId !== searchReqRef.current) return;
        setSuggestions(Array.isArray(matches) ? matches : []);
        setIsLoading(false);
      })
      .catch(() => {
        if (reqId !== searchReqRef.current) return;
        setSuggestions([]);
        setIsLoading(false);
      });
  }, []);

  // Load the per-item stats whenever a product is selected.
  React.useEffect(() => {
    if (!selectedItem) {
      setItemStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    const reqId = ++statsReqRef.current;
    setStatsLoading(true);
    setStatsError(null);

    statsApi
      .singleItem(selectedItem.id)
      .then((res) => {
        if (reqId !== statsReqRef.current) return;
        setItemStats(res);
        setStatsLoading(false);
      })
      .catch((err) => {
        if (reqId !== statsReqRef.current) return;
        setStatsError(
          errorMessage(err, "Couldn't load this item's statistics.")
        );
        setStatsLoading(false);
      });
  }, [selectedItem]);

  const handleQueryChange = React.useCallback((event) => {
    setQuery(event.target.value);
    setSelectedItem(null);
  }, []);

  const renderSuggestion = React.useCallback(
    (item, closePanel) => (
      <button
        key={item.id || `${item.sku}-${item.name}`}
        type="button"
        className={styles.suggestionRow}
        onClick={() => {
          setQuery(item.name);
          setSelectedItem(item);
          closePanel();
        }}
        title={`Use "${item.name}" as the search term.`}
      >
        <span className={styles.suggestionLabel}>Item:</span>
        <span className={styles.suggestionPill}>
          {item.name} (SKU: {item.sku})
        </span>
      </button>
    ),
    []
  );

  return (
    <div className={styles.singleItem}>
      <div className={styles.toolbar}>
        <div className={styles.searchLabel}>
          Search for Products by Name or SKU Number
        </div>
        <div className={styles.searchInput}>
          <SuggestionInput
            type="search"
            placeholder="Search Products"
            aria-label="Search Products"
            value={query}
            onChange={handleQueryChange}
            fetchSuggestions={fetchSuggestions}
            suggestions={suggestions}
            isLoading={isLoading}
            minLength={1}
          >
            {renderSuggestion}
          </SuggestionInput>
        </div>
      </div>

      {selectedItem ? (
        <div className={styles.selectionContent}>
          <header className={styles.selectionHeader}>
            <h1 className={styles.selectionTitle}>{selectedItem.name}</h1>
          </header>

          {statsError ? (
            <AlertNotice title="Couldn't load item statistics">
              {statsError}
            </AlertNotice>
          ) : statsLoading || !itemStats ? (
            <Loader size={60} />
          ) : (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>General</h2>

              <div className={styles.totalsRow}>
                {(itemStats.totals || []).map((card) => (
                  <StatsTotalCard
                    key={card.id}
                    title={card.title}
                    total={card.total}
                    sixWeekAvg={card.sixWeekAvg}
                  />
                ))}
              </div>

              <div className={styles.generalRow}>
                <CustomerActivityGraph
                  data={itemStats.customerActivity?.data}
                  activeDay={itemStats.customerActivity?.activeDay}
                  title="Customer Activity"
                />

                <StatsPieChart data={itemStats.pie} />
              </div>
            </section>
          )}
        </div>
      ) : (
        <section className={styles.emptyState}>
          <h2 className={styles.emptyTitle}>No Product selected</h2>
          <p className={styles.emptySubtitle}>
            But you can use the search box above to find one.
          </p>
        </section>
      )}

      {!selectedItem && <SearchTips />}
    </div>
  );
}
